import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminListTickets } from '../../api/tickets';
import type { AdminListTicketsParams } from '../../api/tickets';
import type {
  Ticket,
  TicketCategory,
  TicketStatus,
} from '../../types/ticket';
import Breadcrumb from '../../components/Breadcrumb';
import StatusBadge from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

const PAGE_SIZE = 20;
const ACTIVE_STATUSES = 'open,in_progress,resolved';
const ACTIVE_SUB_STATUSES: TicketStatus[] = ['open', 'in_progress', 'resolved'];
const ALL_CATEGORIES: TicketCategory[] = [
  'script_upload', 'episode_processing', 'asset_analysis', 'base_image',
  'shot_generation', 'state_image_prompt', 'video_generation', 'account_credits', 'other',
];

type Tab = 'active' | 'closed';

export default function AdminTicketManager() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('active');
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [category, setCategory] = useState<TicketCategory | ''>('');
  const [q, setQ] = useState('');
  const [committedQ, setCommittedQ] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params: AdminListTicketsParams = { page, limit: PAGE_SIZE };
        if (tab === 'closed') {
          params.status = 'closed';
        } else if (status) {
          params.status = status;
        } else {
          params.status = ACTIVE_STATUSES;
        }
        if (category) params.category = category;
        if (committedQ) params.q = committedQ;
        const res = await adminListTickets(params);
        if (!ignore) {
          setList(res.data);
          setTotal(res.meta.total);
        }
      } catch (e) {
        if (!ignore) {
          const msg = e instanceof Error ? e.message : t('tickets.loadFailed');
          toast.error(msg);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [tab, status, category, committedQ, page, toast, t]);

  const switchTab = (next: Tab): void => {
    if (next === tab) return;
    setTab(next);
    setStatus('');
    setPage(1);
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setCommittedQ(q);
      if (page !== 1) setPage(1);
    }
  };

  const onRowKey = (e: React.KeyboardEvent<HTMLTableRowElement>, ticketId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/tickets/${ticketId}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.65rem 0.5rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'var(--text-3)',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface-2)',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.65rem 0.5rem',
    fontSize: '0.88rem',
    borderBottom: '1px solid var(--border)',
  };

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('tickets.breadcrumbHome'), to: '/projects' }, { label: t('tickets.title') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('tickets.title')}</h1>
      </div>

      <div
        role="tablist"
        aria-label={t('tickets.title')}
        style={{
          display: 'flex',
          gap: '0.25rem',
          borderBottom: '1px solid var(--border)',
          marginBottom: '1rem',
        }}
      >
        {([
          { key: 'active', label: t('tickets.activeTickets') },
          { key: 'closed', label: t('tickets.closed') },
        ] as const).map((tabItem) => {
          const active = tab === tabItem.key;
          return (
            <button
              key={tabItem.key}
              role="tab"
              aria-selected={active}
              onClick={() => switchTab(tabItem.key)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '0.6rem 1rem',
                fontSize: '0.9rem',
                color: active ? 'var(--primary)' : 'var(--text-2)',
                fontWeight: active ? 600 : 400,
                borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {tabItem.label}
            </button>
          );
        })}
      </div>

      <div className="card admin-tickets-filters" style={{ padding: '0.9rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'nowrap', alignItems: 'center' }}>
          {tab === 'active' && (
            <select
              aria-label={t('tickets.statusFilter')}
              className="input"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as TicketStatus | '');
                if (page !== 1) setPage(1);
              }}
              style={{ minWidth: 140 }}
            >
              <option value="">{t('tickets.allActive')}</option>
              {ACTIVE_SUB_STATUSES.map((k) => (
                <option key={k} value={k}>
                  {t(`status.${k}`, { ns: 'tickets' })}
                </option>
              ))}
            </select>
          )}
          <select
            aria-label={t('tickets.categoryFilter')}
            className="input"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value as TicketCategory | '');
              if (page !== 1) setPage(1);
            }}
            style={{ minWidth: 140 }}
          >
            <option value="">{t('tickets.allCategories')}</option>
            {ALL_CATEGORIES.map((k) => (
              <option key={k} value={k}>
                {t(`category.${k}`, { ns: 'tickets' })}
              </option>
            ))}
          </select>
          <input
            aria-label={t('tickets.searchTitle')}
            className="input"
            placeholder={t('tickets.searchTitle')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKey}
            style={{ minWidth: 220, flex: '0 1 260px' }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('loading', { ns: 'common' })}</div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('tickets.noTickets')}</div>
      ) : (
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('tickets.status')}</th>
                <th style={thStyle}>{t('tickets.category')}</th>
                <th style={thStyle}>{t('tickets.titleColumn')}</th>
                <th style={thStyle}>{t('tickets.submitter')}</th>
                <th style={thStyle}>{t('tickets.lastActivity')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((ticket) => (
                <tr
                  key={ticket.id}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                  onKeyDown={(e) => onRowKey(e, ticket.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <td style={tdStyle}>
                    <StatusBadge
                      status={ticket.status}
                      label={t(`status.${ticket.status}`, { ns: 'tickets', defaultValue: ticket.status })}
                    />
                  </td>
                  <td style={tdStyle}>{t(`category.${ticket.category}`, { ns: 'tickets', defaultValue: ticket.category })}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{ticket.title}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-2)' }}>{ticket.userName}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    {formatDate(ticket.lastActivityAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
        }}
      >
        <span>{t('tickets.total', { count: total })}</span>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.8rem' }}
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          {t('tickets.prevPage')}
        </button>
        <span>
          {page} / {totalPages}
        </span>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.8rem' }}
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages}
        >
          {t('tickets.nextPage')}
        </button>
      </div>
    </div>
  );
}
